import { useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Flag } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as admin from '@/api/admin';
import type { AdminFeatureFlag } from '@/api/admin';
import { toApiError } from '@/api/client';
import { LoadingScreen } from '@/components/LoadingScreen';

export const Route = createFileRoute('/_authenticated/admin/feature-flags/')({
  component: FeatureFlagsPage,
});

type Toast = { message: string; severity: 'success' | 'error' };

function FeatureFlagsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<Toast | null>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const flagsQ = useQuery({ queryKey: ['feature-flags'], queryFn: admin.listFeatureFlags });

  const mut = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) => admin.setFeatureFlag(key, enabled),
    onMutate: ({ key }) => setPendingKey(key),
    onSuccess: (flag) => {
      void queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      setToast({ message: `“${flag.key}” ${flag.enabled ? 'activado' : 'desactivado'}.`, severity: 'success' });
    },
    onError: (err) => setToast({ message: toApiError(err).message, severity: 'error' }),
    onSettled: () => setPendingKey(null),
  });

  if (flagsQ.isLoading) return <LoadingScreen message="Cargando feature flags…" />;

  const flags = flagsQ.data ?? [];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Flag color="primary" /> Feature flags
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Capacidades de plataforma que se pueden prender o apagar globalmente. (v1: store in-memory.)
        </Typography>
      </Box>

      {flags.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            No hay feature flags definidos.
          </Typography>
        </Paper>
      ) : (
        <Paper variant="outlined">
          <List disablePadding>
            {flags.map((flag: AdminFeatureFlag, i) => (
              <ListItem
                key={flag.key}
                divider={i < flags.length - 1}
                secondaryAction={
                  pendingKey === flag.key ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Switch
                      edge="end"
                      checked={flag.enabled}
                      disabled={mut.isPending}
                      onChange={(e) => mut.mutate({ key: flag.key, enabled: e.target.checked })}
                      inputProps={{ 'aria-label': `Toggle ${flag.key}` }}
                    />
                  )
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                        {flag.key}
                      </Typography>
                    </Stack>
                  }
                  secondary={flag.description}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} variant="filled">
          {toast?.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
